import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import ItemImage from '@components/cart/ProductImage';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import AddToCart from '@components/shared/AddToCart';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import FadeIn from '@components/shared/FadeIn';
import PriceDisplay from '@components/shared/PriceDisplay';
import { P } from '@components/shared/Typography';
import { CartItem, NavStatus, ProductCode } from '@contracts';
import { getProductLink, getProductSubtitle, getProductTitle } from '@data/products';
import { getCartItemType } from '@utils/cart';
import { getProductPrice } from '@utils/price';
import useNavigate from '@utils/useNavigate';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Props {
  productCode: ProductCode;
}

const CartCrossSellRow: React.FC<Props> = ({ productCode }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { state } = useContext(Context);
  const { navDispatch } = useContext(NavContext);
  const { currency, country } = state;
  const [price, setPrice] = useState(-1);
  const titleText = useMemo(() => getProductTitle(productCode), [productCode]);
  const subtitleText = useMemo(() => getProductSubtitle(productCode), [productCode]);
  const titleHtml = useReplacedInnerHtml(titleText);
  const descriptionHtml = useReplacedInnerHtml(subtitleText);
  const cartItemType = getCartItemType(productCode);

  useEffect(() => {
    handleFetchProductPrice();
  }, [productCode]);

  const customiseYourBox = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navigateWrapper('/products/tampons/');
  };

  const handleFetchProductPrice = async () => {
    const getPrice = await getProductPrice(productCode, currency, country);
    setPrice(getPrice.price);
  };

  const handleProductClick = () => {
    const url: string = getProductLink(productCode) ?? '';
    if (url === '') return;
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navigateWrapper(url);
  };

  return (
    <Container data-testid="cross-sell-row">
      <FadeIn duration={800}>
        <ItemImage id={productCode} />
        <ItemInfo>
          <ItemTitle>
            <ProductText onClick={handleProductClick}>
              <span data-testid="product-title" dangerouslySetInnerHTML={{ __html: titleHtml }} />
              <span className="subtitle" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
            </ProductText>
          </ItemTitle>
          {productCode === ProductCode.TamponBox ? (
            <ItemPriceButton>
              <ButtonStyled onClick={customiseYourBox}>{t('components.choose-box')}</ButtonStyled>
            </ItemPriceButton>
          ) : (
            <ItemPriceButton>
              <CartItemPrice>
                <PriceDisplay price={price} variant="crossSell" />
              </CartItemPrice>
              {price > -1 ? (
                <AddToCartStyled cartItem={new CartItem(productCode, productCode, price, 1, cartItemType)} textKey={t('components.add')} />
              ) : null}
            </ItemPriceButton>
          )}
        </ItemInfo>
      </FadeIn>
    </Container>
  );
};

export default CartCrossSellRow;

const Container = styled.div`
  padding: 0;
  margin-top: 15px;

  &:first-child {
    margin-top: 0;
  }

  & > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const ItemTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-grow: 1;
`;

const ItemPriceButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const ProductText = styled.h5`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  line-height: 1.2;
  font-weight: normal;
  font-family: 'BrownStd';
  color: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }

  .subtitle {
    font-size: 13px;
    line-height: 16px;
    opacity: 0.5;

    &:empty {
      display: none;
    }
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CartItemPrice = styled(P)`
  display: flex;
  flex-direction: column-reverse;
  font-size: 16px;
  line-height: 19px;
  font-family: 'BrownStd';
  font-weight: 700;
  text-align: left;
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 17px;
    min-width: 54px;
    text-align: center;
  }
`;

const AddToCartStyled = styled(AddToCart)`
  border: 0;
  background: ${Colors.paleGold};
  color: inherit;
  border-radius: 2px;
  height: 31px;
  padding: 0 10px;
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 1px;
  text-align: center;
  cursor: pointer;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 15px;
    padding: 0 8px;
  }

  &:hover {
    background: ${Colors.navyBlue};
    color: ${Colors.ecru};
  }
`;

const ButtonStyled = styled(Button)`
  width: 100%;
  border: 0;
  background: ${Colors.paleGold};
  color: ${Colors.navyBlue};
  border-radius: 2px;
  height: 31px;
  padding: 0 10px;
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 1px;
  text-align: center;
  cursor: pointer;
  text-transform: uppercase;

  &:hover {
    background: ${Colors.navyBlue};
    color: ${Colors.ecru};
  }

  @media (max-width: 768px) {
    padding: 0 10px;
    font-size: 12px;
    line-height: 15px;
  }
`;
